import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    Title,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Filler,
} from "chart.js";
import {
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    SelectValue,
} from "components/ui/select";
import { Skeleton } from "components/ui/skeleton";
import { toast } from "components/ui/toast";
import {
    BarChart3, TrendingUp, Plus,
    LayoutTemplate, Users as UsersIcon, FileText,
    ArrowRight, Wallet, CreditCard, Calendar, CheckCircle2,
    Mail, MousePointerClick, AlertTriangle, ShieldAlert,
    Activity, Clock, Trophy, TrendingDown,
} from "lucide-react";
import { getDashboardRes } from "core";
import { useBalance, useUserInfo } from "core";
import CampaignStatsDialog from "components/campaigns/CampaignStatsDialog";
import { DashboardKPIGrid, EmptyState } from "./ModernKPICards";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { ErrorState } from "components/ui/error-state";

// ===== ChartJS Registration =====
ChartJS.register(
    ArcElement, Tooltip, Legend, Title,
    CategoryScale, LinearScale, PointElement,
    LineElement, BarElement, Filler,
);

// ===== Helper =====
const getStartTimeISOString = (interval: string) => {
    const now = new Date();
    switch (interval) {
        case "Yesterday": now.setDate(now.getDate() - 1); break;
        case "Today": now.setHours(0, 0, 0, 0); break;
        case "Last Week": now.setDate(now.getDate() - 7); break;
        case "Last Month": now.setMonth(now.getMonth() - 1); break;
        case "Last 6 Month": now.setMonth(now.getMonth() - 6); break;
        case "Last Year": now.setFullYear(now.getFullYear() - 1); break;
        case "Last 2 Year": now.setFullYear(now.getFullYear() - 2); break;
    }
    return now.toISOString();
};

// ===== Greeting helper =====
function getGreeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
}

// ===== Section Card Wrapper =====
const _SectionCard: React.FC<{
    title?: string;
    subtitle?: string;
    action?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
    noPad?: boolean;
    animDelay?: number;
}> = ({ title, subtitle: _subtitle, action, children, className = "", noPad, animDelay = 0 }) => (
    <div
        className={`bg-surface dark:bg-dark-800 rounded-xl border border-line dark:border-dark-700
            shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden
            ${className}`}
        style={{ animationDelay: `${animDelay}ms` }}
    >
        {(title || action) && (
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
                <div>
                    {title && <h3 className="text-sm font-semibold text-content dark:text-gray-100">{title}</h3>}
                </div>
                {action}
            </div>
        )}
        <div className={noPad ? "" : "px-5 pb-5"}>{children}</div>
    </div>
);
export default function Dashboard() {
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const { data: userData } = useUserInfo();

    const [statsFilter, setStatsFilter] = useState({ type: "All Time", startTime: null as string | null });
    const [dashboardData, setDashboardData] = useState<any>({});
    const [campaignData, setCampaignData] = useState<any>({});
    const [lastTransaction, setLastTransaction] = useState<any>(null);
    const [funnelData, setFunnelData] = useState<any>(null);
    const [numberOfCampaigns, setNumberOfCampaigns] = useState<number>(0);
    const [campaignStatusStats, setCampaignStatusStats] = useState<{ active: number; inProgress: number; completed: number } | null>(null);
    const [weeklyData, setWeeklyData] = useState<number[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    /*
     * The fetch used to swallow failure into a toast and then render as if the
     * account were empty — so on the landing page a failed request read as
     * "no campaigns, no funnel data" rather than "we couldn't reach the
     * server". The toast is kept; the page now also says so, with a retry.
     */
    const [loadError, setLoadError] = useState<unknown>(null);
    const [campaignStatsDetails, setCampaignStatsDetails] = useState<any>(null);
    const [latestTemplate, setLatestTemplate] = useState<any>(null);
    const { balance } = useBalance();

    const statusFilterConstant = [
        "All Time", "Today", "Yesterday", "Last Week",
        "Last Month", "Last 6 Month", "Last Year", "Last 2 Year",
    ];

    /* ─── User name for greeting ─── */
    const userName = useMemo(() => {
        const name = userData?.attrs?.name || userData?.attrs?.email?.split("@")[0];
        return name ? name.charAt(0).toUpperCase() + name.slice(1) : null;
    }, [userData]);

    /* ─── Load Dashboard ─── */
    const getDashboard = useCallback(async (filterVal?: any) => {
        try {
            setIsLoading(true);
            setLoadError(null);
            const res = await getDashboardRes(filterVal);
            const data = res.data;
            const emailStats = data.Email_Statistics || {};

            setNumberOfCampaigns(data.Number_of_campaigns || 0);
            setDashboardData(emailStats);
            setLastTransaction(data.Last_Transaction || null);
            setCampaignData({
                recentCampaign: data.recentCampaign,
                mostPopularCampaign: data.mostPopularCampaign,
                leastPopularCampaign: data.leastPopularCampaign,
            });

            // Campaign status breakdown (active / inProgress / completed)
            const total = data.Number_of_campaigns || 0;
            const rawStats = data.Status_based_campaigns_stats || {};
            if (total > 0) {
                setCampaignStatusStats({
                    active: rawStats.active || 0,
                    inProgress: rawStats.inProgress || 0,
                    completed: rawStats.completed || 0,
                });
            } else {
                setCampaignStatusStats(null);
            }

            const weeks = data.Number_of_active_campaigns_week_wise || [];
            setWeeklyData(weeks);
            setFunnelData(data.funnelGraphData || null);
            setLatestTemplate(data.Latest_template || null);
        } catch (err) {
            setLoadError(err ?? new Error("Dashboard request failed"));
            toast.error("Something went wrong!");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { getDashboard(); }, [getDashboard]);

    /* ─── Filter ─── */
    const setStatsFilterHandler = (type: string) => {
        if (type === statsFilter.type) return;
        const startTime = type === "All Time" ? null : getStartTimeISOString(type);
        setStatsFilter({ type, startTime });
        getDashboard({ type, startTime });
    };

    /* ─── Stats Modal ─── */
    const openStats = (key: string) => {
        const d = campaignData[key];
        if (d) { setCampaignStatsDetails(d); }
        else toast.error(`${key} data not available`);
    };

    /* ─── Chart theme colors ─── */
    const gridColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
    const tickColor = isDark ? "#cbd5e1" : "#4b5563";
    const tooltipBg = isDark ? "#0f172a" : "#111827";

    /* ─── Funnel Area Chart ─── */
    const funnelChartData = useMemo(() => {
        if (!funnelData) return null;
        const delivered = funnelData.delivered ?? 0;
        const opens = funnelData.unique_opens ?? 0;
        const clicks = funnelData.unique_clicks ?? 0;
        const unsub = funnelData.email_unsubscribed ?? 0;

        // No data at all — hide chart
        if (delivered + opens + clicks + unsub === 0) return null;

        // If delivered=0 but other engagement values exist, use raw values directly
        // (don't enforce monotonic funnel — it would set all to 0)
        const hasDelivered = delivered > 0;
        let values: number[];
        if (hasDelivered) {
            // Proper funnel: each stage ≤ previous
            const v1 = Math.min(opens, delivered);
            const v2 = Math.min(clicks, v1);
            const v3 = Math.min(unsub, v2);
            values = [delivered, v1, v2, v3];
        } else {
            // No delivery data — show raw engagement counts as-is
            values = [opens, clicks, unsub, 0].slice(0, 4);
            // Remove trailing zeros to avoid misleading chart
            while (values.length > 1 && values[values.length - 1] === 0) values.pop();
            // Pad back to 4 for consistent chart shape
            while (values.length < 4) values.push(0);
        }

        const labels = hasDelivered
            ? ["Delivered", "Opened", "Clicked", "Unsubscribed"]
            : ["Opened", "Clicked", "Unsubscribed", "—"];

        return {
            labels,
            datasets: [{
                label: "Engagement",
                data: values,
                fill: true,
                borderColor: "#6366f1",
                backgroundColor: isDark
                    ? "rgba(99,102,241,0.15)"
                    : "rgba(99,102,241,0.10)",
                pointBackgroundColor: "#6366f1",
                pointBorderColor: isDark ? "#1e293b" : "#ffffff",
                pointBorderWidth: 2.5,
                pointRadius: 5,
                pointHoverRadius: 8,
                pointHoverBorderWidth: 2,
                tension: 0,
            }],
        };
    }, [funnelData, isDark]);

    /* ─── Weekly Bar Chart ─── */
    const weeklyBarData = useMemo(() => {
        const hasData = weeklyData.some(v => v > 0);
        if (!hasData) return null;
        // Normalize to exactly 4 weeks, padding with 0 to avoid Chart.js legend issues
        const paddedWeeks = [0, 1, 2, 3].map(i => weeklyData[i] ?? 0);
        const barColors = isDark
            ? ["rgba(99,102,241,0.85)", "rgba(16,185,129,0.85)", "rgba(139,92,246,0.85)", "rgba(245,158,11,0.85)"]
            : ["rgba(99,102,241,0.9)", "rgba(16,185,129,0.9)", "rgba(139,92,246,0.9)", "rgba(245,158,11,0.9)"];
        return {
            labels: ["Week 1", "Week 2", "Week 3", "Week 4"],
            datasets: [{
                label: "Active Campaigns",
                data: paddedWeeks,
                backgroundColor: barColors,
                hoverBackgroundColor: isDark
                    ? ["rgba(99,102,241,1)", "rgba(16,185,129,1)", "rgba(139,92,246,1)", "rgba(245,158,11,1)"]
                    : ["rgba(99,102,241,1)", "rgba(16,185,129,1)", "rgba(139,92,246,1)", "rgba(245,158,11,1)"],
                borderRadius: 8,
                borderSkipped: false,
                barThickness: 28,
            }],
        };
    }, [weeklyData, isDark]);

    /* ─── Funnel chart options ─── */
    const funnelOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: tooltipBg,
                titleColor: "#f1f5f9",
                bodyColor: "#cbd5e1",
                borderColor: isDark ? "#334155" : "#e2e8f0",
                borderWidth: 1,
                cornerRadius: 10,
                padding: 12,
                titleFont: { size: 13, weight: "bold" as const },
                bodyFont: { size: 12 },
                callbacks: {
                    label: (ctx: any) => ` ${ctx.parsed.y.toLocaleString()}`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: tickColor, font: { size: 13 } },
                border: { display: false },
            },
            y: {
                beginAtZero: true,
                grid: { color: gridColor, drawBorder: false },
                ticks: {
                    color: tickColor,
                    font: { size: 13 },
                    maxTicksLimit: 5,
                    precision: 0,
                    stepSize: 1,
                },
                border: { display: false, dash: [4, 4] },
            },
        },
        interaction: { mode: "index" as const, intersect: false },
    }), [gridColor, tickColor, tooltipBg, isDark]);

    /* ─── Bar chart options ─── */
    const barOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: tooltipBg,
                titleColor: "#f1f5f9",
                bodyColor: "#cbd5e1",
                borderColor: isDark ? "#334155" : "#e2e8f0",
                borderWidth: 1,
                cornerRadius: 10,
                padding: 12,
                titleFont: { size: 13, weight: "bold" as const },
                bodyFont: { size: 12 },
                callbacks: {
                    label: (ctx: any) => ` ${ctx.parsed.y} campaign${ctx.parsed.y !== 1 ? "s" : ""}`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: tickColor, font: { size: 13 } },
                border: { display: false },
            },
            y: {
                beginAtZero: true,
                grid: { color: gridColor },
                ticks: {
                    color: tickColor,
                    font: { size: 13 },
                    stepSize: 1,
                    maxTicksLimit: 5,
                    precision: 0,
                },
                border: { display: false },
            },
        },
        interaction: { mode: "index" as const, intersect: false },
    }), [gridColor, tickColor, tooltipBg, isDark]);

    /* ─── Quick Actions ─── */
    const quickActions = [
        {
            label: "Create Campaign",
            icon: Plus,
            iconBg: "bg-blue-500",
            iconShadow: "shadow-blue-200 dark:shadow-blue-900/40",
            accent: "before:bg-blue-500",
            hoverBg: "hover:bg-blue-50/60 dark:hover:bg-blue-900/10",
            path: "/campaigns/new",
        },
        {
            label: "Add Leads",
            icon: UsersIcon,
            iconBg: "bg-emerald-500",
            iconShadow: "shadow-emerald-200 dark:shadow-emerald-900/40",
            accent: "before:bg-emerald-500",
            hoverBg: "hover:bg-emerald-50/60 dark:hover:bg-emerald-900/10",
            path: "/leads",
        },
        {
            label: "New Template",
            icon: LayoutTemplate,
            iconBg: "bg-violet-500",
            iconShadow: "shadow-violet-200 dark:shadow-violet-900/40",
            accent: "before:bg-violet-500",
            hoverBg: "hover:bg-violet-50/60 dark:hover:bg-violet-900/10",
            path: "/templates",
        },
        {
            label: "View Reports",
            icon: FileText,
            iconBg: "bg-amber-500",
            iconShadow: "shadow-amber-200 dark:shadow-amber-900/40",
            accent: "before:bg-amber-500",
            hoverBg: "hover:bg-amber-50/60 dark:hover:bg-amber-900/10",
            path: "/history",
        },
    ];

    /* ─── Recent campaign entries — always 3 fixed slots ─── */
    const campaignSlots = useMemo(() => [
        {
            label: "Recent Campaign",
            key: "recentCampaign",
            status: "Recent",
            data: campaignData?.recentCampaign ?? null,
            icon: "📊",
            btnBg: "bg-blue-50 hover:bg-blue-100 border border-blue-200 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 dark:border-blue-800",
            btnBgEmpty: "bg-gray-50 border border-line dark:bg-dark-700/40 dark:border-dark-600",
            iconColor: "text-blue-600 dark:text-blue-400",
            textColor: "text-blue-700 dark:text-blue-300",
        },
        {
            label: "Most Popular",
            key: "mostPopularCampaign",
            status: "Popular",
            data: campaignData?.mostPopularCampaign ?? null,
            icon: "↗",
            btnBg: "bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/30 dark:border-emerald-800",
            btnBgEmpty: "bg-gray-50 border border-line dark:bg-dark-700/40 dark:border-dark-600",
            iconColor: "text-emerald-600 dark:text-emerald-400",
            textColor: "text-emerald-700 dark:text-emerald-300",
        },
        {
            label: "Least Popular",
            key: "leastPopularCampaign",
            status: "Low",
            data: campaignData?.leastPopularCampaign ?? null,
            icon: "↘",
            btnBg: "bg-red-50 hover:bg-red-100 border border-red-200 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:border-red-800",
            btnBgEmpty: "bg-gray-50 border border-line dark:bg-dark-700/40 dark:border-dark-600",
            iconColor: "text-red-600 dark:text-red-400",
            textColor: "text-red-700 dark:text-red-300",
        },
    ], [campaignData]);

    /* ─── Parse "Friendly Name <email@x.com>" → display string ─── */
    const parseEmailUsed = (raw: string | null | undefined): string | null => {
        if (!raw) return null;
        // Extract email from "Name <email>" format
        const match = raw.match(/<([^>]+)>/);
        const email = match ? match[1] : raw;
        // Extract friendly name (part before "<")
        const namePart = raw.includes("<") ? raw.split("<")[0].trim() : "";
        return namePart ? `${namePart} · ${email}` : email;
    };

    /* ─── Email Statistics derived values ─── */
    const emailStats = useMemo(() => {
        const recentCampaign = campaignData?.recentCampaign;
        return {
            latestSentEmail: parseEmailUsed(recentCampaign?.email_used),
            /* Was `parseEmailUsed(recentCampaign?.email_used)` — the SENDER'S
               address, under a row labelled "Opens (Last Campaign)". Asking
               how many people opened the last campaign returned an email
               address. The count is right there in the same object. */
            latestOpenedCount: recentCampaign?.pie_chart_data?.unique_opens ?? null,
            bounceRate: typeof dashboardData?.Bounce_Rate === "number" ? dashboardData.Bounce_Rate : null,
            spamRate: typeof dashboardData?.Spam_Rate === "number" ? dashboardData.Spam_Rate : null,
            campaignOpenedRate: recentCampaign?.get_campaign_stats_rates?.opened_rate ?? null,
            campaignClickRate: recentCampaign?.get_campaign_stats_rates?.total_links_clicked ?? null,
            recentStatus: recentCampaign?.c_status || null,
            recentCampaignName: recentCampaign?.name || null,
        };
    }, [campaignData, dashboardData]);

    if (loadError && !isLoading) {
        return (
            <ErrorState
                title="Couldn't load your dashboard"
                description="The request didn't complete. Your campaigns and contacts are unaffected — this is only the overview."
                error={loadError}
                onRetry={() => getDashboard(statsFilter?.startTime ? statsFilter : undefined)}
            />
        );
    }

    return (
        <div className="space-y-6">

            {/* ─── Page Header with Greeting ─── */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-[22px] sm:text-display-sm font-bold text-content dark:text-white tracking-tight leading-tight">
                        {userName ? `${getGreeting()} 👋` : "Dashboard"}
                    </h1>
                    <p className="text-[13px] font-medium text-content-muted dark:text-gray-300 mt-1">
                        Your marketing performance at a glance
                    </p>
                </div>
                <Select value={statsFilter.type} onValueChange={setStatsFilterHandler}>
                    <SelectTrigger
                        className="w-full sm:w-[160px] h-9 rounded-lg border border-line dark:border-dark-600
                             dark:bg-dark-800 text-[13px] font-medium text-gray-700 dark:text-gray-200
                            hover:border-line-strong dark:hover:border-dark-500
                            focus:ring-2 focus:ring-accent/20 focus:ring-offset-0
                            transition-all justify-between"
                    >
                        <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                        {statusFilterConstant.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* ─── KPI Cards ─── */}
            <DashboardKPIGrid
                dashboardData={dashboardData}
                isLoading={isLoading}
                numberOfCampaigns={numberOfCampaigns}
                funnelData={funnelData}
                recentCampaignStats={campaignData?.recentCampaign?.get_campaign_stats_rates}
                campaignTrends={{
                    campaignsTrend: null,
                    deliveredTrend: null,
                    openRateTrend: null,
                    clickRateTrend: null,
                }}
            />

            {/* ─── Charts Row ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                {/* ── Email Engagement Funnel ── */}
                <div className="bg-surface dark:bg-dark-800 rounded-xl border border-line dark:border-dark-700 shadow-sm overflow-hidden">
                    {/* accent bar */}
                    <div className="h-[3px] bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-500" />
                    <div className="px-5 pt-4 pb-3 flex items-center gap-3 border-b border-gray-50 dark:border-dark-700/60">
                        <div className="p-1.5 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg flex-shrink-0">
                            <BarChart3 className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-title-sm font-semibold text-gray-800 dark:text-gray-100 leading-none">Email Engagement</h3>
                        </div>
                        {funnelData && !isLoading && funnelChartData && (
                            <span className="flex-shrink-0 text-[12px] font-semibold px-2 py-0.5 rounded-full
                                bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400">
                                {funnelData.delivered > 0
                                    ? `${funnelData.delivered.toLocaleString()} delivered`
                                    : funnelData.unique_clicks > 0
                                        ? `${funnelData.unique_clicks} click${funnelData.unique_clicks !== 1 ? "s" : ""} tracked`
                                        : `${funnelData.unique_opens} open${funnelData.unique_opens !== 1 ? "s" : ""} tracked`}
                            </span>
                        )}
                    </div>
                    <div className="px-5 pt-3 pb-5 h-[300px]">
                        {isLoading ? (
                            <Skeleton className="h-full w-full rounded-lg" />
                        ) : funnelChartData ? (
                            <Line data={funnelChartData} options={funnelOptions} />
                        ) : (
                            <EmptyState
                                icon={<BarChart3 className="w-6 h-6" />}
                                title="No funnel data yet"
                                description="Launch your first campaign to see engagement metrics."
                            />
                        )}
                    </div>
                </div>

                {/* ── Campaign Performance (Weekly Bar) ── */}
                <div className="bg-surface dark:bg-dark-800 rounded-xl border border-line dark:border-dark-700 shadow-sm overflow-hidden">
                    {/* accent bar */}
                    <div className="h-[3px] bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-500" />
                    <div className="px-5 pt-4 pb-3 flex items-center gap-3 border-b border-gray-50 dark:border-dark-700/60">
                        <div className="p-1.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex-shrink-0">
                            <TrendingUp className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-title-sm font-semibold text-gray-800 dark:text-gray-100 leading-none">Campaign Performance</h3>
                        </div>
                        {weeklyBarData && !isLoading && (
                            <div className="flex items-center gap-2.5 flex-shrink-0">
                                {[
                                    { label: "W1", color: "bg-indigo-500" },
                                    { label: "W2", color: "bg-emerald-500" },
                                    { label: "W3", color: "bg-violet-500" },
                                    { label: "W4", color: "bg-amber-500" },
                                ].map(({ label, color }) => (
                                    <span key={label} className="flex items-center gap-1">
                                        <span className={`w-2 h-2 rounded-full ${color}`} />
                                        <span className="text-[13px] font-medium text-content-secondary dark:text-gray-300">{label}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="px-5 pt-3 pb-5 h-[300px]">
                        {isLoading ? (
                            <Skeleton className="h-full w-full rounded-lg" />
                        ) : weeklyBarData ? (
                            <Bar data={weeklyBarData} options={barOptions} />
                        ) : (
                            <EmptyState
                                icon={<TrendingUp className="w-6 h-6" />}
                                title="No weekly data"
                                description="Start campaigns to see weekly performance trends."
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* ─── Bottom Row: Campaigns + Quick Actions + Account ─── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                {/* ── Campaign Highlights ── */}
                <div className="bg-surface dark:bg-dark-800 rounded-xl border border-line dark:border-dark-700 shadow-sm overflow-hidden">
                    <div className="h-[3px] bg-gradient-to-r from-blue-500 to-indigo-500" />
                    <div className="px-4 pt-3.5 pb-2.5 border-b border-gray-50 dark:border-dark-700/60 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex-shrink-0">
                                <BarChart3 className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                            </div>
                            <h3 className="text-title-sm font-semibold text-gray-800 dark:text-gray-100 leading-none">Campaign Highlights</h3>
                        </div>
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full
                            bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400
                            border border-blue-100 dark:border-blue-800/40 flex-shrink-0">
                            {numberOfCampaigns} total
                        </span>
                    </div>
                    <div className="p-3 space-y-1.5">
                        {isLoading ? (
                            Array(3).fill(0).map((_, i) => (
                                <div key={i} className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg bg-gray-50 dark:bg-dark-700/40">
                                    <Skeleton className="h-7 w-7 rounded-lg flex-shrink-0" />
                                    <div className="flex-1 space-y-1">
                                        <Skeleton className="h-3 w-2/3 rounded" />
                                        <Skeleton className="h-2.5 w-1/2 rounded" />
                                    </div>
                                </div>
                            ))
                        ) : campaignSlots.every(slot => !slot.data) ? (
                            <div className="py-8 flex flex-col items-center gap-2">
                                <Clock className="w-7 h-7 text-gray-300 dark:text-gray-600" />
                                <p className="text-[13px] font-medium text-content-muted dark:text-gray-400">No campaigns yet</p>
                            </div>
                        ) : (
                            campaignSlots.map((slot) => {
                                const SlotIcon = slot.key === "recentCampaign" ? Clock : slot.key === "mostPopularCampaign" ? Trophy : TrendingDown;
                                const hasData = !!slot.data;
                                return hasData ? (
                                    /* ── Has data → clickable row ── */
                                    <button
                                        key={slot.key}
                                        onClick={() => openStats(slot.key)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                                            transition-all duration-150 text-left group
                                            ${slot.btnBg} hover:shadow-sm cursor-pointer`}
                                    >
                                        <div className="w-7 h-7 flex-shrink-0 rounded-md flex items-center justify-center">
                                            <SlotIcon className={`w-3.5 h-3.5 ${slot.iconColor}`} strokeWidth={2.5} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className={`text-meta font-semibold uppercase tracking-wide leading-snug ${slot.textColor}`}>
                                                {slot.label}
                                            </p>
                                            <p className="text-ui font-medium text-gray-700 dark:text-gray-200 truncate leading-snug">
                                                {slot.data?.name}
                                            </p>
                                        </div>
                                        <ArrowRight className={`w-3.5 h-3.5 flex-shrink-0 opacity-0 group-hover:opacity-60
                                            group-hover:translate-x-0.5 transition-all duration-150 ${slot.iconColor}`} />
                                    </button>
                                ) : (
                                    /* ── No data → dimmed placeholder row ── */
                                    <div
                                        key={slot.key}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                                            bg-gray-50/60 dark:bg-dark-700/30
                                            border border-dashed border-line dark:border-dark-600/60
                                            opacity-60 cursor-default"
                                    >
                                        <div className="w-7 h-7 flex-shrink-0 rounded-md flex items-center justify-center
                                            bg-gray-100 dark:bg-dark-600/50">
                                            <SlotIcon className="w-3.5 h-3.5 text-content-subtle dark:text-gray-500" strokeWidth={2} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-meta font-semibold uppercase tracking-wide leading-snug text-content-subtle dark:text-gray-500">
                                                {slot.label}
                                            </p>
                                            <p className="text-meta text-content-subtle dark:text-gray-500 italic leading-snug">
                                                Data not available
                                            </p>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* ── Quick Actions ── */}
                <div className="bg-surface dark:bg-dark-800 rounded-xl border border-line dark:border-dark-700 shadow-sm overflow-hidden">
                    <div className="h-[3px] bg-gradient-to-r from-violet-500 to-purple-500" />
                    <div className="px-4 pt-3.5 pb-2.5 border-b border-gray-50 dark:border-dark-700/60">
                        <h3 className="text-[15px] font-semibold text-gray-700 dark:text-gray-200">Quick Actions</h3>
                    </div>
                    <div className="p-3 grid grid-cols-2 gap-2.5">
                        {quickActions.map((action) => {
                            const Icon = action.icon;
                            return (
                                <button
                                    key={action.label}
                                    onClick={() => navigate(action.path)}
                                    className="flex flex-col items-start gap-2.5 p-3 rounded-xl
                                        hover:bg-gray-50 dark:hover:bg-dark-700/60
                                        border border-line-subtle dark:border-dark-700/60
                                        hover:border-line dark:hover:border-dark-600
                                        transition-all duration-150 text-left group focus:outline-none
                                        hover:shadow-sm"
                                >
                                    <div className={`w-8 h-8 flex-shrink-0 rounded-lg ${action.iconBg}
                                        flex items-center justify-center
                                        group-hover:scale-105 transition-transform duration-150`}>
                                        <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
                                    </div>
                                    <div className="min-w-0 w-full">
                                        <p className="text-ui-lg font-semibold text-gray-700 dark:text-gray-100 leading-snug">
                                            {action.label}
                                        </p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ── Account Overview ── */}
                <div className="bg-surface dark:bg-dark-800 rounded-xl border border-line dark:border-dark-700 shadow-sm overflow-hidden">
                    {/* Emerald accent top bar */}
                    <div className="h-[3px] bg-gradient-to-r from-emerald-400 to-teal-500" />

                    {isLoading ? (
                        <div className="p-4 space-y-3">
                            <Skeleton className="h-10 w-full rounded-lg" />
                            <Skeleton className="h-3 w-3/4 rounded" />
                            <Skeleton className="h-3 w-1/2 rounded" />
                        </div>
                    ) : (
                        <>
                            {/* Header: icon + title + balance pill */}
                            <div className="px-4 pt-3.5 pb-3 flex items-center gap-2.5 border-b border-gray-50 dark:border-dark-700/60">
                                <div className="p-1.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex-shrink-0">
                                    <Wallet className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-title-sm font-semibold text-gray-800 dark:text-gray-100 leading-none">Account Overview</h3>
                                </div>
                                {/* Balance badge */}
                                <div className="flex-shrink-0 flex flex-col items-end">
                                    <span className="text-meta font-semibold text-emerald-500 dark:text-emerald-400 uppercase tracking-widest leading-none mb-0.5">Balance</span>
                                    <span className="text-[18px] font-bold text-emerald-600 dark:text-emerald-400 leading-none">{balance ?? "—"}</span>
                                </div>
                            </div>

                            {/* Transaction rows */}
                            <div className="px-3 pt-2.5 pb-2">
                                {lastTransaction ? (
                                    <>
                                        {[
                                            {
                                                icon: Calendar, iconBg: "bg-blue-50 dark:bg-blue-900/20", iconColor: "text-blue-500 dark:text-blue-400",
                                                label: "Last Payment", value: lastTransaction?.paymentDate || "—",
                                                valueClass: "text-gray-700 dark:text-gray-200"
                                            },
                                            {
                                                icon: CreditCard, iconBg: "bg-emerald-50 dark:bg-emerald-900/20", iconColor: "text-emerald-500 dark:text-emerald-400",
                                                label: "Amount", value: `$${lastTransaction?.Amount ?? "—"}`,
                                                valueClass: "text-emerald-600 dark:text-emerald-400 font-bold"
                                            },
                                            {
                                                icon: CheckCircle2,
                                                iconBg: ["succeeded", "paid", "success"].includes(lastTransaction?.Status?.toLowerCase())
                                                    ? "bg-emerald-50 dark:bg-emerald-900/20" : "bg-amber-50 dark:bg-amber-900/20",
                                                iconColor: ["succeeded", "paid", "success"].includes(lastTransaction?.Status?.toLowerCase())
                                                    ? "text-emerald-500 dark:text-emerald-400" : "text-amber-500 dark:text-amber-400",
                                                label: "Status",
                                                value: lastTransaction?.Status || "—",
                                                valueClass: ["succeeded", "paid", "success"].includes(lastTransaction?.Status?.toLowerCase())
                                                    ? "text-emerald-600 dark:text-emerald-400 font-semibold" : "text-amber-600 dark:text-amber-400 font-semibold"
                                            },
                                        ].map(({ icon: RowIcon, iconBg, iconColor, label, value: val, valueClass }) => (
                                            <div key={label} className="flex items-center gap-2 py-2 border-b border-gray-50 dark:border-dark-700/50 last:border-0">
                                                <div className={`p-1 ${iconBg} rounded-md flex-shrink-0`}>
                                                    <RowIcon className={`w-3 h-3 ${iconColor}`} />
                                                </div>
                                                <span className="text-[13px] font-medium text-content-secondary dark:text-gray-300 flex-1">{label}</span>
                                                <span className={`text-[13px] font-semibold ${valueClass}`}>{val}</span>
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    <p className="py-4 text-[13px] font-medium text-content-secondary dark:text-gray-300 text-center">No transactions yet</p>
                                )}
                            </div>

                            <div className="px-3 pb-3">
                                <button
                                    /* Straight to the tab. /transactions-history still
                                       resolves but only as a redirect, and routing
                                       through it puts a URL in history that the back
                                       button then has to step over. */
                                    onClick={() => navigate("/payments?tab=transactions")}
                                    className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg
                                        text-[13px] font-semibold text-content-secondary dark:text-gray-300
                                        bg-gray-50 dark:bg-dark-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/10
                                        hover:text-emerald-600 dark:hover:text-emerald-400
                                        border border-line-subtle dark:border-dark-600 hover:border-emerald-200 dark:hover:border-emerald-800/50
                                        transition-colors duration-150 group"
                                >
                                    View Transactions
                                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-150" />
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* ─── Email Statistics + Latest Template + Campaign Status ─── */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">

                {/* ── Email Statistics ── */}
                <div className="bg-surface dark:bg-dark-800 rounded-xl border border-line dark:border-dark-700 shadow-sm overflow-hidden">
                    <div className="h-[3px] bg-gradient-to-r from-indigo-500 to-violet-500" />
                    <div className="px-4 pt-3.5 pb-2.5 border-b border-gray-50 dark:border-dark-700/60 flex items-center gap-2">
                        <div className="p-1.5 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg flex-shrink-0">
                            <Mail className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                        </div>
                        <div>
                            <h3 className="text-title-sm font-semibold text-gray-800 dark:text-gray-100 leading-none">Email Statistics</h3>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="px-4 py-3 space-y-2.5">
                            {Array(4).fill(0).map((_, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <Skeleton className="h-6 w-6 rounded-md flex-shrink-0" />
                                    <Skeleton className="h-2.5 w-28 rounded flex-1" />
                                    <Skeleton className="h-2.5 w-20 rounded" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="px-3 py-1">
                            {[
                                {
                                    label: "Latest Sent From",
                                    value: emailStats.latestSentEmail || "—",
                                    icon: Mail,
                                    iconBg: "bg-blue-50 dark:bg-blue-900/20",
                                    iconColor: "text-blue-500 dark:text-blue-400",
                                    valueClass: "text-gray-700 dark:text-gray-200 truncate max-w-[180px]",
                                    badge: null,
                                },
                                {
                                    label: "Opens (Last Campaign)",
                                    value:
                                        emailStats.latestOpenedCount != null
                                            ? `${emailStats.latestOpenedCount.toLocaleString()} opens`
                                            : "Not measured yet",
                                    icon: MousePointerClick,
                                    iconBg: "bg-violet-50 dark:bg-violet-900/20",
                                    iconColor: "text-violet-500 dark:text-violet-400",
                                    valueClass:
                                        emailStats.latestOpenedCount != null
                                            ? "text-gray-700 dark:text-gray-200 tabular-nums"
                                            : "text-content-subtle dark:text-gray-500 italic",
                                    badge: null,
                                },
                                {
                                    label: "Bounce Rate",
                                    value: emailStats.bounceRate !== null ? `${emailStats.bounceRate.toFixed(2)}%` : "—",
                                    icon: AlertTriangle,
                                    iconBg: emailStats.bounceRate !== null && emailStats.bounceRate > 5 ? "bg-red-50 dark:bg-red-900/20" : "bg-gray-50 dark:bg-dark-700",
                                    iconColor: emailStats.bounceRate !== null && emailStats.bounceRate > 5 ? "text-red-400" : "text-gray-300 dark:text-gray-600",
                                    valueClass: emailStats.bounceRate !== null && emailStats.bounceRate > 5 ? "text-red-600 dark:text-red-400 font-bold" : "text-content-secondary dark:text-gray-300",
                                    badge: emailStats.bounceRate !== null && emailStats.bounceRate > 5
                                        ? { text: "HIGH", cls: "bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400" } : null,
                                },
                                {
                                    label: "Spam Rate",
                                    value: emailStats.spamRate !== null ? `${emailStats.spamRate.toFixed(2)}%` : "—",
                                    icon: ShieldAlert,
                                    iconBg: emailStats.spamRate !== null && emailStats.spamRate > 0.1 ? "bg-amber-50 dark:bg-amber-900/20" : "bg-gray-50 dark:bg-dark-700",
                                    iconColor: emailStats.spamRate !== null && emailStats.spamRate > 0.1 ? "text-amber-400" : "text-gray-300 dark:text-gray-600",
                                    valueClass: emailStats.spamRate !== null && emailStats.spamRate > 0.1 ? "text-amber-600 dark:text-amber-400 font-bold" : "text-content-secondary dark:text-gray-300",
                                    badge: emailStats.spamRate !== null && emailStats.spamRate > 0.1
                                        ? { text: "RISK", cls: "bg-amber-50 dark:bg-amber-900/20 text-amber-500 dark:text-amber-400" } : null,
                                },
                                {
                                    label: "Last Campaign Status",
                                    value: emailStats.recentStatus
                                        ? emailStats.recentStatus.charAt(0).toUpperCase() + emailStats.recentStatus.slice(1).toLowerCase()
                                        : "—",
                                    icon: CheckCircle2,
                                    iconBg: emailStats.recentStatus === "COMPLETED" ? "bg-emerald-50 dark:bg-emerald-900/20"
                                        : emailStats.recentStatus === "ACTIVE" ? "bg-blue-50 dark:bg-blue-900/20"
                                            : "bg-gray-50 dark:bg-dark-700",
                                    iconColor: emailStats.recentStatus === "COMPLETED" ? "text-emerald-500 dark:text-emerald-400"
                                        : emailStats.recentStatus === "ACTIVE" ? "text-blue-500 dark:text-blue-400"
                                            : "text-content-subtle",
                                    valueClass: emailStats.recentStatus === "COMPLETED" ? "text-emerald-600 dark:text-emerald-400 font-semibold"
                                        : emailStats.recentStatus === "ACTIVE" ? "text-blue-600 dark:text-blue-400 font-semibold"
                                            : "text-content-muted dark:text-gray-400",
                                    badge: null,
                                },
                            ].map(({ label, value, icon: RowIcon, iconBg, iconColor, valueClass, badge }) => (
                                <div key={label} className="flex items-center gap-2 py-2
                                    border-b border-gray-50 dark:border-dark-700/50 last:border-0">
                                    <div className={`p-1 ${iconBg} rounded-md flex-shrink-0`}>
                                        <RowIcon className={`w-3 h-3 ${iconColor}`} strokeWidth={2} />
                                    </div>
                                    <span className="text-[13px] font-medium text-content-secondary dark:text-gray-300 flex-1 min-w-0">{label}</span>
                                    {badge && (
                                        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide flex-shrink-0 ${badge.cls}`}>
                                            {badge.text}
                                        </span>
                                    )}
                                    <span className={`text-[13px] font-semibold flex-shrink-0 ${valueClass}`}>{value}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Latest Template ── */}
                <div className="bg-surface dark:bg-dark-800 rounded-xl border border-line dark:border-dark-700 shadow-sm overflow-hidden">
                    <div className="h-[3px] bg-gradient-to-r from-violet-500 to-fuchsia-500" />
                    <div className="px-4 pt-3.5 pb-2.5 border-b border-gray-50 dark:border-dark-700/60 flex items-center gap-2">
                        <div className="p-1.5 bg-violet-50 dark:bg-violet-900/20 rounded-lg flex-shrink-0">
                            <LayoutTemplate className="w-3.5 h-3.5 text-violet-500 dark:text-violet-400" />
                        </div>
                        <h3 className="text-title-sm font-semibold text-gray-800 dark:text-gray-100 leading-none">Latest Template</h3>
                    </div>
                    {isLoading ? (
                        <div className="px-4 py-3 space-y-2.5">
                            {Array(3).fill(0).map((_, i) => (
                                <Skeleton key={i} className="h-5 w-full rounded" />
                            ))}
                        </div>
                    ) : latestTemplate ? (
                        <div className="px-4 py-4 space-y-3">
                            {/* Template name */}
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                                    <LayoutTemplate className="w-5 h-5 text-white" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-ui-lg font-bold text-content dark:text-white truncate">{latestTemplate.name}</p>
                                    {latestTemplate.tag && (
                                        <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[11px] font-semibold
                                            bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400
                                            border border-violet-100 dark:border-violet-800/40">
                                            {latestTemplate.tag.trim()}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {/* Description */}
                            {latestTemplate.Description ? (
                                <p className="text-meta text-content-muted dark:text-gray-400 leading-relaxed line-clamp-2">
                                    {latestTemplate.Description}
                                </p>
                            ) : (
                                <p className="text-[12px] text-content-subtle dark:text-gray-500 italic">No description provided</p>
                            )}
                            {/* Navigate */}
                            <button
                                onClick={() => navigate("/templates")}
                                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg
                                    text-[13px] font-semibold text-violet-600 dark:text-violet-400
                                    bg-violet-50 dark:bg-violet-900/10 hover:bg-violet-100 dark:hover:bg-violet-900/20
                                    border border-violet-100 dark:border-violet-800/40
                                    transition-colors duration-150 group"
                            >
                                View All Templates
                                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-150" />
                            </button>
                        </div>
                    ) : (
                        <div className="py-8 flex flex-col items-center gap-2">
                            <LayoutTemplate className="w-7 h-7 text-gray-300 dark:text-gray-600" />
                            <p className="text-[13px] text-content-muted dark:text-gray-400">No templates yet</p>
                            <button
                                onClick={() => navigate("/templates")}
                                className="text-[12px] font-semibold text-violet-500 dark:text-violet-400 hover:text-violet-600 dark:hover:text-violet-300 transition-colors"
                            >
                                Create your first →
                            </button>
                        </div>
                    )}
                </div>

                {/* ── Campaign Status ── */}
                <div className="bg-surface dark:bg-dark-800 rounded-xl border border-line dark:border-dark-700 shadow-sm overflow-hidden">
                    <div className="h-[3px] bg-gradient-to-r from-blue-500 to-cyan-400" />
                    <div className="px-4 pt-3.5 pb-2.5 border-b border-gray-50 dark:border-dark-700/60 flex items-center gap-2">
                        <div className="p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex-shrink-0">
                            <Activity className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-title-sm font-semibold text-gray-800 dark:text-gray-100 leading-none">Campaign Status</h3>
                        </div>
                        <span className="text-[12px] font-semibold px-2 py-0.5 rounded-full
                            bg-gray-100 dark:bg-dark-700 text-content-secondary dark:text-gray-300 flex-shrink-0">
                            {numberOfCampaigns} total
                        </span>
                    </div>

                    {isLoading ? (
                        <div className="px-4 py-3 space-y-3.5">
                            {Array(3).fill(0).map((_, i) => (
                                <div key={i} className="space-y-1.5">
                                    <div className="flex items-center gap-2">
                                        <Skeleton className="h-2 w-2 rounded-full flex-shrink-0" />
                                        <Skeleton className="h-2.5 w-20 rounded flex-1" />
                                        <Skeleton className="h-2.5 w-10 rounded" />
                                    </div>
                                    <Skeleton className="h-1.5 w-full rounded-full" />
                                </div>
                            ))}
                        </div>
                    ) : campaignStatusStats ? (
                        <div className="px-4 py-3 space-y-3.5">
                            {[
                                { label: "Active", count: campaignStatusStats.active, bar: "bg-emerald-500", track: "bg-emerald-50 dark:bg-emerald-900/20", dot: "bg-emerald-500", badge: "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400" },
                                { label: "In Progress", count: campaignStatusStats.inProgress, bar: "bg-blue-500", track: "bg-blue-50 dark:bg-blue-900/20", dot: "bg-blue-500", badge: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400" },
                                { label: "Completed", count: campaignStatusStats.completed, bar: "bg-slate-400 dark:bg-slate-400", track: "bg-gray-200 dark:bg-dark-600", dot: "bg-slate-400", badge: "bg-gray-100 dark:bg-dark-600 text-content-muted dark:text-gray-300" },
                            ].map(({ label, count, bar, track, dot, badge }) => {
                                const pct = numberOfCampaigns > 0 ? Math.round((count / numberOfCampaigns) * 100) : 0;
                                return (
                                    <div key={label}>
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
                                            <span className="text-[13px] font-medium text-content-secondary dark:text-gray-300 flex-1">{label}</span>
                                            <span className={`text-[12px] font-semibold px-1.5 py-0.5 rounded-full ${badge}`}>{count}</span>
                                            <span className="text-[13px] font-medium text-content-secondary dark:text-gray-300 w-9 text-right">{pct}%</span>
                                        </div>
                                        <div className={`h-1.5 w-full ${track} rounded-full overflow-hidden`}>
                                            <div className={`h-full ${bar} rounded-full transition-all duration-700 ease-out`}
                                                style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="py-10 flex flex-col items-center gap-2">
                            <Activity className="w-7 h-7 text-content-subtle dark:text-gray-400" />
                            <p className="text-[13px] font-medium text-content-secondary dark:text-gray-300">No campaign data yet</p>
                        </div>
                    )}
                </div>
            </div>

            {/* ─── Stats Modal ─── */}
            <CampaignStatsDialog
                campaign={campaignStatsDetails}
                onClose={() => setCampaignStatsDetails(null)}
            />
        </div>
    );
}
